/**
 * Tabla de Contenidos Automática — Frontend interaction (view.js)
 *
 * Responsibilities
 * ────────────────
 * 1. ID injection fallback
 *    Heading blocks that appear BEFORE the TOC block in the document
 *    never pass through the PHP render_block filter (which runs only for
 *    subsequent blocks).  This script scans every heading on the page,
 *    applies the identical slug algorithm, and injects any missing id
 *    attributes so that all TOC anchor links resolve correctly.
 *
 * 2. Collapse / expand
 *    When the block is in "collapsible" mode the PHP render outputs a
 *    <button aria-expanded="true"> next to the list.  This script wires
 *    the click handler, toggles aria-expanded, and adds / removes the
 *    CSS class that hides the list.
 *
 * Slug algorithm
 * ──────────────
 * makeAnchor() is intentionally byte-for-byte identical to:
 *   - waiis_toc_make_anchor() in render.php
 *   - makeAnchor() in index.js (editor preview)
 * Keeping all three in sync ensures that auto-generated slugs always match.
 *
 * Vanilla JS — zero external dependencies.
 * Build target: ES2017 (see build.cjs — esbuild target array).
 *
 * @package waiis
 */

'use strict';

/* ─── Utility: generate anchor slug ─────────────────────────────────────── */

/**
 * Generate a URL-safe anchor slug from heading plain text.
 *
 * @param  {string} text Plain text (no HTML tags).
 * @return {string}      Slug, e.g. "mi-titulo".  Returns "section" on empty.
 */
function makeAnchor( text ) {
	var slug = ( text || '' )
		.normalize( 'NFD' )                // Decompose accented characters.
		.replace( /[\u0300-\u036f]/g, '' ) // Strip diacritic marks.
		.toLowerCase()
		.replace( /[^a-z0-9\s-]/g, '' )   // Keep alphanum, spaces, hyphens.
		.trim()
		.replace( /\s+/g, '-' );           // Collapse whitespace → hyphens.

	return slug || 'section';
}

/**
 * Normalise class input from data-target-class.
 *
 * @param  {string} raw Raw class value.
 * @return {string}     Sanitized class token.
 */
function normalizeClassToken( raw ) {
	var token = ( raw || '' )
		.trim()
		.replace( /^[.#]+/, '' )
		.split( /\s+/ )[ 0 ] || '';

	return token.replace( /[^A-Za-z0-9_-]/g, '' );
}

/**
 * Resolve headings that belong to the TOC scope.
 *
 * @param  {HTMLElement} tocBlock Current TOC block element.
 * @return {HTMLElement[]}        Scoped heading elements in document order.
 */
function getScopedHeadings( tocBlock ) {
	var targetClass = normalizeClassToken( tocBlock.getAttribute( 'data-target-class' ) || '' );
	var allHeadings = Array.prototype.slice.call(
		document.querySelectorAll( 'h1, h2, h3, h4, h5, h6' )
	);

	if ( ! targetClass ) {
		return allHeadings;
	}

	return allHeadings.filter( function( heading ) {
		return heading.closest( '.' + targetClass );
	} );
}

/* ─── Phase 1: ID injection fallback ────────────────────────────────────── */

/**
 * Scan every heading element in the document.
 *
 * For headings that already have an id attribute, record the id in the
 * deduplication tracker to keep the counter in sync with render.php.
 *
 * For headings without an id, compute the slug and — if a TOC link
 * points to that slug — inject the id attribute.
 *
 * This ensures that headings BEFORE the TOC block (which PHP cannot reach)
 * receive their correct id values.
 */
function injectMissingHeadingIds() {
	var tocBlocks = document.querySelectorAll( '.waiis-toc' );
	if ( ! tocBlocks.length ) {
		return;
	}

	tocBlocks.forEach( function( toc ) {
		// Collect unresolved anchor targets referenced by this TOC block.
		var missingAnchors = new Set();

		toc.querySelectorAll( '.waiis-toc__link' ).forEach( function( link ) {
			var href = link.getAttribute( 'href' );
			if ( href && href.charAt( 0 ) === '#' ) {
				var id = href.slice( 1 );
				if ( id && ! document.getElementById( id ) ) {
					missingAnchors.add( id );
				}
			}
		} );

		if ( ! missingAnchors.size ) {
			return; // All links in this TOC already resolve.
		}

		// Walk scoped headings in DOM order, mirroring PHP deduplication.
		var seenSlugs = {};
		var headings  = getScopedHeadings( toc );

		headings.forEach( function( heading ) {
			if ( heading.id ) {
				// Already has an id — record it to keep the counter in sync.
				seenSlugs[ heading.id ] = ( seenSlugs[ heading.id ] || 0 ) + 1;
				return;
			}

			// Compute the canonical slug for this heading text.
			var base  = makeAnchor( heading.textContent || '' );
			var slug  = base;
			var count = 0;

			// Deduplicate exactly as PHP does (append -1, -2, …).
			while ( seenSlugs[ slug ] ) {
				count++;
				slug = base + '-' + count;
			}
			seenSlugs[ slug ] = 1;

			// Only inject if this slug is one of the unresolved TOC targets.
			if ( missingAnchors.has( slug ) ) {
				heading.id = slug;
				missingAnchors.delete( slug );
			}
		} );
	} );
}

/* ─── Phase 2: collapse / expand ────────────────────────────────────────── */

/**
 * Initialise the collapse / expand behaviour on a single TOC block.
 *
 * Expects the following DOM structure (rendered by render.php):
 *   <nav id="waiis-toc-{id}" class="waiis-toc …"
 *        aria-labelledby="waiis-toc-{id}-title">
 *     <div class="waiis-toc__header">
 *       <h2 id="waiis-toc-{id}-title" class="waiis-toc__title">…</h2>
 *       <button class="waiis-toc__toggle" type="button"
 *               aria-expanded="true" aria-controls="waiis-toc-{id}-list">
 *         <span class="waiis-toc__toggle-label">Colapsar</span>
 *         <span class="waiis-toc__toggle-icon" aria-hidden="true">…</span>
 *       </button>
 *     </div>
 *     <ol id="waiis-toc-{id}-list" class="waiis-toc__list">…</ol>
 *   </nav>
 *
 * @param {HTMLElement} tocBlock The .waiis-toc <nav> element.
 */
function initCollapsible( tocBlock ) {
	var btn  = tocBlock.querySelector( '.waiis-toc__toggle' );
	var list = tocBlock.querySelector( '.waiis-toc__list' );

	if ( ! btn || ! list ) {
		return;
	}

	var labelEl = btn.querySelector( '.waiis-toc__toggle-label' );

	/**
	 * Update aria-expanded attribute and the visible button label text.
	 *
	 * @param {boolean} expanded The new expanded state.
	 */
	function applyState( expanded ) {
		btn.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );

		if ( labelEl ) {
			// i18n-ready: these strings are plain text, not l10n'd here
			// since view.js has no wp.i18n dependency.  Translate at the
			// PHP layer (render.php) if needed for dynamic label changes.
		labelEl.textContent = expanded ? 'Colapsar' : 'Desplegar';
		}
	}

	/**
	 * Toggle the visible state of the list.
	 *
	 * Uses the native `hidden` attribute instead of a CSS class so that:
	 *  - Browsers remove the subtree from the accessibility tree automatically.
	 *  - Keyboard Tab navigation skips links inside a [hidden] element natively,
	 *    satisfying WCAG 2.1 SC 2.1.1 and SC 1.3.1.
	 *  - No CSS dependency is required for the hide behaviour itself.
	 */
	function toggle() {
		var isExpanded = btn.getAttribute( 'aria-expanded' ) === 'true';

		// Collapse when currently expanded; expand when currently collapsed.
		list.hidden = isExpanded;
		applyState( ! isExpanded );
	}

	btn.addEventListener( 'click', toggle );
}

/* ─── Bootstrap ──────────────────────────────────────────────────────────── */

/**
 * Main entry point — runs once the DOM is fully parsed.
 */
function init() {
	// Phase 1: ensure all heading IDs are present.
	injectMissingHeadingIds();

	// Phase 2: wire collapsible behaviour where the toggle button exists.
	document.querySelectorAll( '.waiis-toc' ).forEach( function( tocBlock ) {
		if ( tocBlock.querySelector( '.waiis-toc__toggle' ) ) {
			initCollapsible( tocBlock );
		}
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	// DOMContentLoaded has already fired (e.g. deferred script).
	init();
}
